import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Badge,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  PlusCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import RABillBulkUpload from "../../components/upload/RABillBulkUpload";

const { Option } = Select;
const { RangePicker } = DatePicker;

const CreateRABills: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    contractor: "",
    woNumber: "",
    site: "",
    project: "",
    dateRange: null as any,
    minAvailable: "",
  });

  const handleCreateRABill = (woId: string) => {
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-create-detail/${woId}${flowTypeQuery}`
    );
  };

  const resetFilters = () => {
    setFilters({
      contractor: "",
      woNumber: "",
      site: "",
      project: "",
      dateRange: null,
      minAvailable: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  const columns: any = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 220,
      fixed: "left",
      render: (text: string, record: any) => (
        <div>
          <div className="fw-bold text-primary">{text}</div>
          <div className="text-muted small mt-1">{record.contractType}</div>
        </div>
      ),
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 220,
      render: (_: any, record: any) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
          {/* <div className="text-muted small">Section: {record.section}</div> */}
        </div>
      ),
    },
    {
      title: isClientFlow ? "Client" : "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 180,
    },
    {
      title: "WO Total Value",
      dataIndex: "totalValue",
      key: "totalValue",
      width: 150,
      align: "right",
      render: (value: any) => (
        <div className="fw-bold">{formatCurrency(value, "INR")}</div>
      ),
    },
    {
      title: "Work Done %",
      dataIndex: "progressPercent",
      key: "progressPercent",
      width: 120,
      align: "center",
      render: (percentage: any) => {
        const color =
          percentage >= 75
            ? "#52c41a"
            : percentage >= 50
            ? "#1890ff"
            : percentage >= 25
            ? "#faad14"
            : "#ff4d4f";
        return <Tag color={color}>{percentage?.toFixed(1)}%</Tag>;
      },
    },
    {
      title: "Cumulative Work Value",
      dataIndex: "totalExecutedSubTotalValue",
      key: "totalExecutedSubTotalValue",
      width: 160,
      align: "right",
      render: (value: any) => (
        <div className="fw-bold text-success">
          {formatCurrency(value, "INR")}
        </div>
      ),
    },
    // {
    //   title: "Pending Work Value",
    //   dataIndex: "totalPendingApprovalExecutedSubTotalValue",
    //   key: "totalPendingApprovalExecutedSubTotalValue",
    //   width: 160,
    //   align: "right",
    //   render: (value: any) => (
    //     <div className="fw-bold text-success">
    //       {formatCurrency(value, "INR")}
    //     </div>
    //   ),
    // },
    {
      title: "Previous RA Bills",
      dataIndex: "totalBilledSubTotalValue",
      key: "totalBilledSubTotalValue",
      width: 150,
      align: "right",
      render: (value: any) => (
        <div className="text-info">{formatCurrency(value, "INR")}</div>
      ),
    },
    {
      title: "Pending RA Bills",
      dataIndex: "totalPendingApprovalBilledSubTotalValue",
      key: "totalPendingApprovalBilledSubTotalValue",
      width: 150,
      align: "right",
      render: (value: any) => (
        <div className="text-info">{formatCurrency(value, "INR")}</div>
      ),
    },
    {
      title: "Available for Billing",
      dataIndex: "balanceBilledSubTotalValue",
      key: "balanceBilledSubTotalValue",
      width: 160,
      align: "right",
      render: (value: any, record: any) => {
        return (
          <div className="fw-bold text-primary">
            {formatCurrency(value, "INR")}
          </div>
        );
      },
      // sorter: (a:any, b:any) => a.totalExecutedValue - b.totalBilledValue,
    },
    {
      title: "Last RA Bill #",
      dataIndex: "lastRaBillSequenceId",
      key: "lastRaBillSequenceId",
      width: 220,
      render: (text: string, record: any) => {
        if (!text || !record.lastRaBillId) {
          return "---";
        }
        const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
        return (
          <span
            className="text-primary fw-bold"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() =>
              navigate(
                `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.lastRaBillId}?mode=view${flowTypeQuery}`
              )
            }
          >
            {text}
          </span>
        );
      },
    },
    // {
    //   title: "Flags",
    //   key: "flags",
    //   width: 120,
    //   render: (_, record) => (
    //     <div>
    //       {record.softHold && (
    //         <Tag color="orange" title="Soft Hold Applied">SOFT HOLD</Tag>
    //       )}
    //       {record.payOnCompletion && (
    //         <Tag color="blue" title="Pay on Completion">POC</Tag>
    //       )}
    //       {record.accountingPeriod !== 'Open' && (
    //         <Tag color="red" title="Period Closed">CLOSED</Tag>
    //       )}
    //     </div>
    //   ),
    // },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 180,
      render: (_: any, record: any) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<PlusCircleOutlined />}
            onClick={() => handleCreateRABill(record.workOrderId)}
            // disabled={record.hardHold || record.accountingPeriod !== 'Open'}
          >
            Create RA Bill
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="ra-bills-create"
      title="Create RA Bills"
      subtitle="Select eligible Work Orders to create Running Account Bills"
      columns={columns}
      loading={loading}
      topRightJSX={<RABillBulkUpload showAsButton={true} />}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <RABillAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1800,
        showSearch: true,
        searchPlaceholder: "Search by WO#, Contractor, or Site...",
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="ABC Infra Ltd">ABC Infra Ltd</Option>
                    <Option value="XYZ Constructions">XYZ Constructions</Option>
                    <Option value="BuildTech Pvt Ltd">BuildTech Pvt Ltd</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Order #</label>
                  <Input
                    placeholder="Enter WO Number"
                    value={filters.woNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, woNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Site</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Site"
                    value={filters.site}
                    onChange={(value) =>
                      setFilters({ ...filters, site: value })
                    }
                  >
                    <Option value="Urban Rise - Tower A">
                      Urban Rise - Tower A
                    </Option>
                    <Option value="Urban Rise - Tower B">
                      Urban Rise - Tower B
                    </Option>
                    <Option value="Urban Rise - Tower C">
                      Urban Rise - Tower C
                    </Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Project</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Project"
                    value={filters.project}
                    onChange={(value) =>
                      setFilters({ ...filters, project: value })
                    }
                  >
                    <Option value="Tower Construction Phase 1">
                      Tower Construction Phase 1
                    </Option>
                    <Option value="Tower Construction Phase 2">
                      Tower Construction Phase 2
                    </Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Last RA Bill Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                    placeholder={["From Date", "To Date"]}
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Min Available Value</label>
                  <Input
                    placeholder="Enter minimum available value"
                    value={filters.minAvailable}
                    onChange={(e) =>
                      setFilters({ ...filters, minAvailable: e.target.value })
                    }
                    allowClear
                  />
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default CreateRABills;
